import { PERMISSIONS, type Permission, ROLE_LABELS } from '@edms/contracts';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  Building2,
  ChevronDown,
  CopyCheck,
  FileSearch,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  ScanLine,
  ScrollText,
  Settings,
  ShieldCheck,
  Sun,
  Users,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { IconButton } from '@/components/ui/primitives';
import { useAuth } from '@/features/auth/auth-context';
import { useTheme } from '@/features/theme/theme-context';
import { api } from '@/lib/api-client';
import { cn, initialsOf } from '@/lib/utils';
import { NotificationsPanel } from './notifications-panel';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: typeof LayoutDashboard;
  readonly permission?: Permission;
  readonly end?: boolean;
}

interface NavGroup {
  readonly label: string | null;
  readonly items: readonly NavItem[];
}

/**
 * Navigation.
 *
 * Regroupée par intention et non par entité technique : un Branch Leader pense
 * « je numérise », « je traite mes exceptions », « je cherche un reçu ». Les
 * entrées auxquelles l'utilisateur n'a pas droit ne sont pas grisées mais
 * absentes — une interface qui montre ce qu'on ne peut pas faire fatigue sans
 * rien apprendre.
 */
const NAVIGATION: readonly NavGroup[] = [
  {
    label: null,
    items: [
      { to: '/', label: 'Tableau de bord', icon: LayoutDashboard, permission: PERMISSIONS.DASHBOARD_READ, end: true },
      { to: '/recherche', label: 'Recherche', icon: FileSearch, permission: PERMISSIONS.RECEIPTS_READ },
    ],
  },
  {
    label: 'Traitement',
    items: [
      { to: '/numerisation', label: 'Numérisation', icon: ScanLine, permission: PERMISSIONS.LOTS_READ },
      // Les écrans d'exception se règlent sur le droit d'**agir**, pas sur
      // celui de lire : un Area Manager ou un compte de consultation n'a rien à
      // y faire, et leur proposer une file qu'ils ne peuvent pas traiter serait
      // une fausse piste.
      { to: '/exceptions/validation', label: 'Validation', icon: ShieldCheck, permission: PERMISSIONS.RECEIPTS_CORRECT },
      { to: '/exceptions/doublons', label: 'Doublons', icon: CopyCheck, permission: PERMISSIONS.DUPLICATES_RESOLVE },
    ],
  },
  {
    label: 'Administration',
    items: [
      { to: '/administration/utilisateurs', label: 'Utilisateurs', icon: Users, permission: PERMISSIONS.USERS_READ },
      { to: '/administration/bureaux', label: 'Bureaux', icon: Building2, permission: PERMISSIONS.BRANCHES_READ },
      { to: '/administration/zones-ocr', label: 'Zones OCR', icon: ScanLine, permission: PERMISSIONS.OCR_TEMPLATES_READ },
      { to: '/administration/journal', label: "Journal d'audit", icon: ScrollText, permission: PERMISSIONS.AUDIT_READ },
      { to: '/administration/parametres', label: 'Paramètres', icon: Settings, permission: PERMISSIONS.SETTINGS_READ },
    ],
  },
];

export function AppShell(): React.JSX.Element {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // La navigation mobile se referme au changement de page : la laisser ouverte
  // masquerait le contenu qu'on vient d'atteindre.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <div className="flex h-full bg-canvas">
      <aside
        className={cn(
          'no-print fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-line-subtle bg-raised',
          'transition-transform duration-200 lg:static lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <Sidebar onClose={() => setMobileOpen(false)} />
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-[hsl(215_28%_10%_/_0.5)] lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenMenu={() => setMobileOpen(true)} />
        <main className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Sidebar({ onClose }: { onClose: () => void }): React.JSX.Element {
  const { user, can } = useAuth();

  return (
    <>
      <div className="flex h-14 items-center justify-between border-b border-line-subtle px-4">
        <div className="flex items-center gap-2.5">
          <img
            src="/brand/edms-mark-64.png"
            alt=""
            width={28}
            height={28}
            className="size-7 shrink-0"
            aria-hidden
          />
          <div className="leading-none">
            <p className="text-sm font-semibold tracking-tight text-ink">EDMS</p>
            <p className="mt-0.5 text-2xs text-ink-faint">Archivage documentaire</p>
          </div>
        </div>
        <IconButton label="Fermer le menu" size="sm" className="lg:hidden" onClick={onClose}>
          <X className="size-4" />
        </IconButton>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Navigation principale">
        {NAVIGATION.map((group) => {
          const visible = group.items.filter((item) => !item.permission || can(item.permission));
          if (visible.length === 0) return null;

          return (
            <div key={group.label ?? 'principal'} className="mb-5 last:mb-0">
              {group.label && (
                <p className="mb-1.5 px-2.5 text-2xs font-semibold tracking-wide text-ink-faint uppercase">
                  {group.label}
                </p>
              )}
              <ul className="space-y-0.5">
                {visible.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors duration-100',
                          isActive
                            ? 'bg-accent-soft font-medium text-accent-text'
                            : 'text-ink-muted hover:bg-hover hover:text-ink',
                        )
                      }
                    >
                      <item.icon className="size-4 shrink-0" aria-hidden />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      {user && (
        <div className="border-t border-line-subtle px-3 py-3">
          <div className="flex items-center gap-2.5 rounded-md px-1.5 py-1">
            <span
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-2xs font-semibold text-accent-text"
              aria-hidden
            >
              {initialsOf(user.fullName)}
            </span>
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-xs font-medium text-ink">{user.fullName}</p>
              <p className="truncate text-2xs text-ink-faint">
                {ROLE_LABELS[user.role]} · {user.branchCode}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TopBar({ onOpenMenu }: { onOpenMenu: () => void }): React.JSX.Element {
  const { user, logout, canAny } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /*
   * Une notification annonce un travail à faire : un lot archivé, des
   * exceptions à traiter, un incident d'exploitation. Chacune renvoie vers un
   * écran — numérisation, validation, paramètres.
   *
   * Un compte de consultation n'a accès à aucun de ces écrans. Lui présenter
   * une cloche reviendrait à l'avertir de tâches qu'il ne peut ni ouvrir ni
   * traiter. On se règle donc sur le droit d'**agir**, comme la navigation le
   * fait déjà pour les écrans d'exception.
   */
  const voitLesNotifications = canAny(
    PERMISSIONS.LOTS_READ,
    PERMISSIONS.RECEIPTS_CORRECT,
    PERMISSIONS.SETTINGS_READ,
  );

  const { data: unread } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    // Inutile d'interroger le serveur toutes les 45 secondes pour un compte
    // qui ne verra jamais le résultat.
    enabled: voitLesNotifications,
    // Sondage périodique : le §14 demande une notification automatique, sans
    // que l'utilisateur ait à rafraîchir la page.
    refetchInterval: 45_000,
  });

  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (event: MouseEvent): void => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [menuOpen]);

  return (
    <header className="no-print sticky top-0 z-20 flex h-14 shrink-0 items-center gap-3 border-b border-line-subtle bg-raised/85 px-4 backdrop-blur-md">
      <IconButton label="Ouvrir le menu" className="lg:hidden" onClick={onOpenMenu}>
        <Menu className="size-4" />
      </IconButton>

      <div className="flex-1" />

      <IconButton
        label={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
        onClick={toggle}
      >
        {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
      </IconButton>

      {voitLesNotifications && (
        <div className="relative">
          <IconButton label="Notifications" onClick={() => setNotificationsOpen((open) => !open)}>
            <Bell className="size-4" />
            {(unread?.count ?? 0) > 0 && (
              <span
                className="absolute top-1.5 right-1.5 flex size-4 items-center justify-center rounded-full bg-danger text-[9px] font-semibold text-white"
                aria-label={`${unread?.count} notification(s) non lue(s)`}
              >
                {Math.min(9, unread?.count ?? 0)}
              </span>
            )}
          </IconButton>

          {notificationsOpen && <NotificationsPanel onClose={() => setNotificationsOpen(false)} />}
        </div>
      )}

      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          className="flex items-center gap-2 rounded-md py-1 pr-1.5 pl-1 transition-colors hover:bg-hover"
        >
          <span
            className="flex size-7 items-center justify-center rounded-full bg-accent-soft text-2xs font-semibold text-accent-text"
            aria-hidden
          >
            {user ? initialsOf(user.fullName) : '?'}
          </span>
          <ChevronDown className="size-3.5 text-ink-faint" aria-hidden />
        </button>

        {menuOpen && (
          <div
            role="menu"
            className="anim-rise absolute right-0 z-30 mt-1.5 w-56 overflow-hidden rounded-lg border border-line bg-raised shadow-[var(--shadow-overlay)]"
          >
            <div className="border-b border-line-subtle px-3.5 py-3">
              <p className="truncate text-sm font-medium text-ink">{user?.fullName}</p>
              <p className="truncate text-xs text-ink-faint">{user?.email}</p>
            </div>
            <MenuItem
              icon={<Settings className="size-4" />}
              label="Changer mon mot de passe"
              onClick={() => {
                setMenuOpen(false);
                navigate('/mon-compte');
              }}
            />
            <MenuItem
              icon={<LogOut className="size-4" />}
              label="Se déconnecter"
              tone="danger"
              onClick={() => void logout()}
            />
          </div>
        )}
      </div>
    </header>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  tone = 'default',
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  tone?: 'default' | 'danger';
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm transition-colors',
        tone === 'danger'
          ? 'text-danger hover:bg-danger-soft'
          : 'text-ink-muted hover:bg-hover hover:text-ink',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
